import { z } from "zod";

/**
 * Recursive fallback for any TipTap node we don't model explicitly.
 *
 * Intentionally permissive — most node types inside a ProseMirror doc (paragraph,
 * text, heading, lists, mentions, code blocks, etc.) are pass-through as far as
 * the deployment-type sync logic is concerned. We only narrow the section /
 * tabContainer / tabPanel envelope below; everything else stays opaque so that
 * future editor extensions can add new node types without breaking validation.
 */
export type TipTapJSONContent = {
  type?: string;
  attrs?: Record<string, unknown>;
  content?: TipTapJSONContent[];
  marks?: Array<{ type: string; attrs?: Record<string, unknown> }>;
  text?: string;
};

const TiptapNodeSchema: z.ZodType<TipTapJSONContent> = z.lazy(() =>
  z
    .object({
      type: z.string().optional(),
      attrs: z.record(z.string(), z.unknown()).optional(),
      content: z.array(TiptapNodeSchema).optional(),
      marks: z
        .array(
          z.object({
            type: z.string(),
            attrs: z.record(z.string(), z.unknown()).optional()
          })
        )
        .optional(),
      text: z.string().optional()
    })
    .passthrough()
);

/**
 * Attributes on a `tabPanel` node — the only node whose `attrs` the sync logic reads
 * and writes (`deploymentTypes`, `tabId`).
 */
export const TabPanelAttrsSchema = z
  .object({
    tabId: z.string(),
    deploymentTypes: z.array(z.string()).default([]),
    deploymentDisplayName: z.string().default("")
  })
  .passthrough();

export const TabPanelNodeSchema = z
  .object({
    type: z.literal("tabPanel"),
    attrs: TabPanelAttrsSchema,
    content: z.array(TiptapNodeSchema).optional()
  })
  .passthrough();

/**
 * Attributes on a `tabContainer` node. The schema is intentionally permissive —
 * legacy docs may still carry view-state attributes from before NEX-2128 dropped
 * them from the persisted contract; `.passthrough()` ignores them without raising.
 */
export const TabContainerAttrsSchema = z.object({}).passthrough();

export const TabContainerNodeSchema = z
  .object({
    type: z.literal("tabContainer"),
    attrs: TabContainerAttrsSchema.optional(),
    content: z.array(TabPanelNodeSchema)
  })
  .passthrough();

/**
 * Attributes on a `section` node. Most flags are optional because production data
 * predates several of them — `.passthrough()` preserves any future additions too.
 */
export const SectionAttrsSchema = z
  .object({
    id: z.string(),
    isDeploymentSpecific: z.boolean().default(false),
    isReadOnly: z.boolean().default(false),
    isHidden: z.boolean().default(false),
    isDefaultConfigSection: z.boolean().default(false)
  })
  .passthrough();

/**
 * A `section` node may contain a single tabContainer plus other arbitrary content
 * (sectionTitle, paragraphs, etc.). We don't narrow the inner content — the sync
 * only inspects the tabContainer entry via runtime checks.
 */
export const SectionNodeSchema = z
  .object({
    type: z.literal("section"),
    attrs: SectionAttrsSchema,
    content: z.array(TiptapNodeSchema)
  })
  .passthrough();

/**
 * Top-level ProseMirror document stored in `Agent.promptJson` and the latest
 * non-checkpoint `AgentPromptVersion.promptJson`. The `content` array mixes section
 * nodes with any other TipTap nodes; the sync iterates and runtime-narrows on
 * `type === "section"` / `type === "tabContainer"`.
 */
export const PromptJsonSchema = z
  .object({
    type: z.literal("doc"),
    content: z.array(TiptapNodeSchema).optional()
  })
  .passthrough();

/**
 * Backward-compat alias. Existing callers (`AgentEntity.fromPrisma`) imported
 * `TiptapDocumentSchema` before the schema was enriched — keep the alias so the
 * migration is mechanical at the call sites that just want the doc envelope.
 */
export const TiptapDocumentSchema = PromptJsonSchema;

export type PromptJson = z.infer<typeof PromptJsonSchema>;
export type TiptapDocument = PromptJson;
export type SectionNode = z.infer<typeof SectionNodeSchema>;
export type TabContainerNode = z.infer<typeof TabContainerNodeSchema>;
export type TabPanelNode = z.infer<typeof TabPanelNodeSchema>;
export type SectionAttrs = z.infer<typeof SectionAttrsSchema>;
export type TabContainerAttrs = z.infer<typeof TabContainerAttrsSchema>;
export type TabPanelAttrs = z.infer<typeof TabPanelAttrsSchema>;
